import { useState, useRef, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  format,
  eachDayOfInterval,
  isToday,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import api from '../lib/api';
import type { CalendarEntry } from '../types';
import { CalendarEntryDialog } from '../pages/calendar/CalendarEntryDialog';
import { cn } from '../lib/utils';

const WEEKDAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function isoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

interface ContextMenu {
  x: number;
  y: number;
  date: string;
}

function useCalendarEntries(days: Date[]) {
  const months = [...new Set(days.map((d) => `${d.getFullYear()}-${d.getMonth() + 1}`))];

  const results = useQueries({
    queries: months.map((key) => {
      const [year, month] = key.split('-').map(Number);
      return {
        queryKey: ['calendar', year, month],
        queryFn: async (): Promise<CalendarEntry[]> => {
          const response = await api.get('/calendar', { params: { year, month } });
          return response.data;
        },
      };
    }),
  });

  const allEntries = results.flatMap((r) => r.data ?? []);
  const seen = new Set<number>();
  return allEntries.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
}

export function WeeklyCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntry | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const wStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const wEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: wStart, end: wEnd });

  const entries = useCalendarEntries(weekDays);

  const entriesForDay = useCallback(
    (day: Date) => {
      const iso = isoDate(day);
      return entries.filter((e) => e.date_start <= iso && e.date_end >= iso);
    },
    [entries],
  );

  const handleDayRightClick = (e: React.MouseEvent, day: Date) => {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    setContextMenu({
      x: e.clientX - (rect?.left ?? 0),
      y: e.clientY - (rect?.top ?? 0),
      date: isoDate(day),
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  const openNewEntry = (date: string) => {
    setSelectedEntry(null);
    setDefaultDate(date);
    setDialogOpen(true);
    closeContextMenu();
  };

  const openEditEntry = (entry: CalendarEntry) => {
    setSelectedEntry(entry);
    setDefaultDate(undefined);
    setDialogOpen(true);
  };

  const handleDialogSuccess = (y: number, m: number) => {
    setCurrentDate(new Date(y, m - 1, 1));
  };

  const navLabel = `KW ${getISOWeek(wStart)} · ${format(wStart, 'dd.', { locale: de })} – ${format(wEnd, 'dd. MMMM yyyy', { locale: de })}`;

  return (
    <Card variant="glass" onClick={closeContextMenu}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-display flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Wochenkalender
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setCurrentDate((d) => subWeeks(d, 1)); }}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold min-w-[240px] text-center">{navLabel}</span>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setCurrentDate((d) => addWeeks(d, 1)); }}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date()); }}>
              Heute
            </Button>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); openNewEntry(isoDate(new Date())); }}>
              <Plus className="h-4 w-4 mr-1" />
              Neuer Eintrag
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4">
        <div ref={containerRef} className="relative select-none">
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day, idx) => {
              const dayEntries = entriesForDay(day);
              const todayDay = isToday(day);
              return (
                <div key={isoDate(day)} className="flex flex-col gap-1">
                  <div
                    className={cn(
                      'text-center py-2 rounded-md text-sm font-semibold',
                      todayDay ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <div className="hidden sm:block">{WEEKDAYS_LONG[idx]}</div>
                    <div className="sm:hidden text-xs">{WEEKDAYS_LONG[idx].slice(0, 2)}</div>
                    <div className={cn('text-lg font-bold', todayDay ? 'text-primary-foreground' : 'text-foreground')}>
                      {format(day, 'd')}
                    </div>
                    <div className="text-xs font-normal opacity-75">
                      {format(day, 'MMM', { locale: de })}
                    </div>
                  </div>

                  <div
                    className="flex-1 min-h-[180px] rounded-md border bg-card p-1.5 space-y-1.5 cursor-default hover:bg-accent/30 transition-colors"
                    onContextMenu={(e) => handleDayRightClick(e, day)}
                  >
                    {dayEntries.length === 0 && (
                      <div className="h-full flex items-start justify-center pt-6 text-xs text-muted-foreground/50">
                        —
                      </div>
                    )}
                    {dayEntries.map((entry) => (
                      <button
                        key={entry.id}
                        className="w-full text-left"
                        onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }}
                      >
                        <div className="rounded-md bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors p-2 space-y-0.5">
                          <div className="text-xs font-bold text-primary truncate">
                            {entry.license_plate}
                          </div>
                          <div className="text-xs font-medium text-foreground truncate">
                            {entry.renter_name}
                          </div>
                          {entry.renter_phone && (
                            <div className="text-xs text-muted-foreground truncate">
                              {entry.renter_phone}
                            </div>
                          )}
                          {entry.price_cents != null && (
                            <div className="text-xs text-muted-foreground">
                              {(entry.price_cents / 100).toLocaleString('de-DE', {
                                style: 'currency',
                                currency: 'EUR',
                              })}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground/70">
                            {format(new Date(entry.date_start + 'T00:00:00'), 'dd.MM.')} –{' '}
                            {format(new Date(entry.date_end + 'T00:00:00'), 'dd.MM.')}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {contextMenu && (
            <div
              className="absolute z-50 bg-popover border rounded-md shadow-lg py-1 min-w-[180px]"
              style={{ left: contextMenu.x, top: contextMenu.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-1.5 text-xs text-muted-foreground border-b mb-1">
                {format(new Date(contextMenu.date + 'T00:00:00'), 'dd. MMMM yyyy', { locale: de })}
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer"
                onClick={() => openNewEntry(contextMenu.date)}
              >
                <Plus className="h-4 w-4" />
                Mietfahrzeug eintragen
              </button>
            </div>
          )}
        </div>

        <CalendarEntryDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          entry={selectedEntry}
          defaultDate={defaultDate}
          onSuccess={handleDialogSuccess}
        />
      </CardContent>
    </Card>
  );
}
